import { STORAGE } from '@webready/shared/config';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { cn } from '@/lib/utils';
import { useDeferredHeroVideo } from '@/hooks/useDeferredHeroVideo';
import { isLighthouseOrBot } from '@/utils/browserDetection';

const HERO_VIDEO_AVAILABILITY_KEY = STORAGE.keys.heroVideoAvailability;

/** Full-quality source — loaded only after LCP / idle (never re-encoded for size) */
export const SITE_HERO_VIDEO =
  (import.meta.env.VITE_SITE_HERO_VIDEO_URL as string | undefined)?.trim() ||
  '/hero_images/hero_video.mp4';

/** Optional compressed source — loaded first for immediate playback if configured */
export const SITE_HERO_VIDEO_SMALL =
  (import.meta.env.VITE_SITE_HERO_VIDEO_SMALL_URL as string | undefined)?.trim() ||
  null;

const SITE_HERO_VIDEO_ENABLED = import.meta.env.VITE_SITE_HERO_VIDEO_ENABLED !== 'false';


export const SITE_HERO_POSTER = '/hero_images/hero_poster.webp';
export const SITE_HERO_POSTER_MOBILE = '/hero_images/hero_poster-768.webp';

/** Mobil: focal pe colțul mesei / birou din dreapta; desktop: centrat-dreapta */
const MEDIA_OBJECT =
  'object-cover object-[90%_40%] md:object-[center_right]';

export const SiteVideoBackdrop = memo(function SiteVideoBackdrop() {
  const smallVideoRef = useRef<HTMLVideoElement>(null);
  const largeVideoRef = useRef<HTMLVideoElement>(null);
  const [location] = useLocation();
  const [smallVideoSource, setSmallVideoSource] = useState<string | null>(null);
  const [largeVideoSource, setLargeVideoSource] = useState<string | null>(null);
  const [smallVideoReady, setSmallVideoReady] = useState(false);
  const [largeVideoReady, setLargeVideoReady] = useState(false);

  const { shouldLoadVideo, onVideoCanPlay, resetVideoVisible } =
    useDeferredHeroVideo();

  const canAttemptVideo = shouldLoadVideo && SITE_HERO_VIDEO_ENABLED && !isLighthouseOrBot();

  useEffect(() => {
    if (canAttemptVideo) {
      setLargeVideoSource(SITE_HERO_VIDEO);
      if (SITE_HERO_VIDEO_SMALL) {
        setSmallVideoSource(SITE_HERO_VIDEO_SMALL);
      }
    } else {
      setSmallVideoSource(null);
      setLargeVideoSource(null);
      setSmallVideoReady(false);
      setLargeVideoReady(false);
    }
  }, [canAttemptVideo]);

  const handleSmallVideoCanPlay = useCallback(() => {
    setSmallVideoReady(true);
    onVideoCanPlay();
  }, [onVideoCanPlay]);

  const handleLargeVideoCanPlay = useCallback(() => {
    setLargeVideoReady(true);
    onVideoCanPlay();
  }, [onVideoCanPlay]);

  const handleSmallVideoError = useCallback(() => {
    setSmallVideoSource(null);
    setSmallVideoReady(false);
  }, []);

  const handleLargeVideoError = useCallback(() => {
    setLargeVideoSource(null);
    setLargeVideoReady(false);
    try {
      sessionStorage.setItem(HERO_VIDEO_AVAILABILITY_KEY, '0');
    } catch {
      /* ignore */
    }
  }, []);

  const resumeVideo = useCallback((video: HTMLVideoElement | null, src: string | null) => {
    if (!video || !src) return;
    if (video.ended) {
      video.currentTime = 0;
    }
    void video.play().catch(() => {
      /* autoplay blocked — poster remains visible */
    });
  }, []);

  const resumeAllPlayback = useCallback(() => {
    if (smallVideoSource && !largeVideoReady) {
      resumeVideo(smallVideoRef.current, smallVideoSource);
    }
    if (largeVideoSource) {
      resumeVideo(largeVideoRef.current, largeVideoSource);
    }
  }, [smallVideoSource, largeVideoSource, largeVideoReady, resumeVideo]);

  // Pause small video when large is ready to save CPU/performance
  useEffect(() => {
    if (largeVideoReady && smallVideoRef.current) {
      try {
        smallVideoRef.current.pause();
      } catch (e) {
        /* ignore */
      }
    }
  }, [largeVideoReady]);

  useEffect(() => {
    if (!largeVideoSource && !smallVideoSource) return;

    resumeAllPlayback();

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        resumeAllPlayback();
      }
    };

    window.addEventListener('pageshow', resumeAllPlayback);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.removeEventListener('pageshow', resumeAllPlayback);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [smallVideoSource, largeVideoSource, resumeAllPlayback, location]);

  useEffect(() => {
    if (!largeVideoSource && !smallVideoSource) {
      resetVideoVisible();
    }
  }, [smallVideoSource, largeVideoSource, resetVideoVisible]);

  const showLargeVideo = !!(largeVideoSource && largeVideoReady);
  const showSmallVideo = !!(smallVideoSource && smallVideoReady && !largeVideoReady);
  const showPoster = !showLargeVideo && !showSmallVideo;

  return (
    <div className="absolute inset-0" aria-hidden>
      <div className="absolute inset-0 bg-[#0d0618]" />

      <img
        src={SITE_HERO_POSTER_MOBILE}
        srcSet={`${SITE_HERO_POSTER_MOBILE} 768w, ${SITE_HERO_POSTER} 1920w`}
        sizes="100vw"
        alt=""
        fetchPriority="high"
        decoding="async"
        width={1920}
        height={1080}
        className={cn(
          'absolute inset-0 h-full w-full',
          MEDIA_OBJECT,
          'transition-opacity duration-700 ease-out',
          showPoster ? 'opacity-100' : 'opacity-0'
        )}
      />

      {smallVideoSource && (
        <video
          ref={smallVideoRef}
          width={1920}
          height={1080}
          className={cn(
            'absolute inset-0 h-full w-full',
            MEDIA_OBJECT,
            'transition-opacity duration-700 ease-out',
            showSmallVideo ? 'opacity-100' : 'opacity-0'
          )}
          autoPlay
          muted
          playsInline
          preload="metadata"
          src={smallVideoSource}
          onCanPlay={handleSmallVideoCanPlay}
          onError={handleSmallVideoError}
        />
      )}

      {largeVideoSource && (
        <video
          ref={largeVideoRef}
          width={1920}
          height={1080}
          className={cn(
            'absolute inset-0 h-full w-full',
            MEDIA_OBJECT,
            'transition-opacity duration-700 ease-out',
            showLargeVideo ? 'opacity-100' : 'opacity-0'
          )}
          autoPlay
          muted
          playsInline
          preload="metadata"
          src={largeVideoSource}
          onCanPlay={handleLargeVideoCanPlay}
          onError={handleLargeVideoError}
          onEnded={(e) => {
            e.currentTarget.pause();
          }}
        />
      )}

      <div
        className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-transparent pointer-events-none md:to-black/25"
        aria-hidden
      />
    </div>
  );
});
